import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import PrivacyPolicyFr from './fr/PrivacyPolicyFr';
import '../styles/Document.css';

function PrivacyPolicy() {
  const { isFr } = useLanguage();
  if (isFr) return <PrivacyPolicyFr />;

  return (
    <div className="doc-page">
      <div className="doc-header">
        <span className="doc-label">Privacy</span>
        <h1 className="doc-title">MegaTransact Privacy Policy</h1>
        <p className="doc-intro">
          How MegaTransact collects, uses, protects, and manages personal information.
          MegaTransact is a digital platform beginning with salary-based lending and expanding
          progressively into practical money services. We are committed to handling personal
          information responsibly, securely, and transparently.
        </p>
        <div className="doc-meta">
          <span className="doc-meta-item">
            <strong>Effective Date:</strong> March 27, 2026
          </span>
          <span className="doc-meta-item">
            <strong>Last Updated:</strong> March 27, 2026
          </span>
        </div>
      </div>

      <div className="doc-body">
        <div className="doc-preamble">
          <p>
            MegaTransact respects your privacy and is committed to handling your personal
            information responsibly. This Privacy Policy explains how MegaTransact collects,
            uses, stores, shares, and protects information when you use our app, website, support
            channels, and related services.
          </p>
          <p>
            MegaTransact is a digital platform beginning with salary-based lending and may expand
            over time into additional money services and related tools. This Privacy Policy applies
            to all users of MegaTransact services unless a separate privacy notice is provided for
            a specific service.
          </p>
        </div>

        {/* Who We Are */}
        <div className="doc-section">
          <h2 className="section-title">Who We Are</h2>
          <p>
            MegaTransact is a digital platform designed to provide structured salary-linked
            financial support and related digital services.
          </p>
          <p>
            If you have questions about this Privacy Policy or about how your information is
            handled, you may contact us at:
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>

        {/* Information We Collect */}
        <div className="doc-section">
          <h2 className="section-title">Information We Collect</h2>
          <p>
            We may collect personal information directly from you, from your employer where
            relevant and permitted, from service providers, and from your use of the platform.
          </p>
          <p>This information may include:</p>
          <ul>
            <li className="cat-item">
              <strong>Identity information</strong>
              <ul>
                <li>Full name</li>
                <li>Date of birth</li>
                <li>Profile photo, where provided</li>
                <li>
                  Identification details such as national ID, passport, employee ID, or other
                  verification documents where needed
                </li>
              </ul>
            </li>
            <li className="cat-item">
              <strong>Contact information</strong>
              <ul>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Residential address</li>
                <li>Emergency or alternate contact details where applicable</li>
              </ul>
            </li>
            <li className="cat-item">
              <strong>Employment and workplace information</strong>
              <ul>
                <li>Employer name</li>
                <li>Job title or position</li>
                <li>Employee number</li>
                <li>Salary range or salary information</li>
                <li>Salary payment schedule</li>
                <li>Employment status</li>
                <li>
                  Payroll-related information relevant to eligibility and repayment
                  arrangements
                </li>
              </ul>
            </li>
            <li className="cat-item">
              <strong>Account and platform information</strong>
              <ul>
                <li>Username</li>
                <li>Login credentials</li>
                <li>Device identifiers</li>
                <li>App usage data</li>
                <li>Transaction references</li>
                <li>Account preferences</li>
                <li>Support history</li>
              </ul>
            </li>
            <li className="cat-item">
              <strong>Financial and repayment-related information</strong>
              <ul>
                <li>Requested amount</li>
                <li>Approved amount</li>
                <li>Repayment schedule</li>
                <li>Repayment history</li>
                <li>Payroll deduction records where applicable</li>
                <li>Payout or disbursement details</li>
                <li>Linked service details relevant to payments or collections</li>
              </ul>
            </li>
            <li className="cat-item">
              <strong>Technical information</strong>
              <ul>
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device type</li>
                <li>Mobile operating system</li>
                <li>Language settings</li>
                <li>Dates and times of access</li>
                <li>Crash logs and app diagnostics</li>
              </ul>
            </li>
            <li className="cat-item">
              <strong>Communications</strong>
              <ul>
                <li>Messages sent to customer support</li>
                <li>Survey responses</li>
                <li>Feedback submissions</li>
                <li>Call or chat records where permitted by law</li>
              </ul>
            </li>
          </ul>
        </div>

        {/* How We Use Your Information */}
        <div className="doc-section">
          <h2 className="section-title">How We Use Your Information</h2>
          <p>
            We use personal information for legitimate operational, service, security, and legal
            purposes, including to:
          </p>
          <ul>
            <li>Create and manage your account</li>
            <li>Verify your identity where required</li>
            <li>Assess eligibility for salary-based support services</li>
            <li>Process applications and related requests</li>
            <li>Facilitate salary-linked repayment arrangements</li>
            <li>Communicate with you about your account or requests</li>
            <li>Provide customer support</li>
            <li>Improve app performance and user experience</li>
            <li>Monitor service usage and prevent misuse</li>
            <li>Maintain records and internal reporting</li>
            <li>
              Comply with legal, regulatory, contractual, and risk-management obligations
            </li>
            <li>
              Investigate fraud, abuse, security issues, or policy violations
            </li>
            <li>Develop and improve current and future services</li>
          </ul>
        </div>

        {/* Legal Basis for Processing */}
        <div className="doc-section">
          <h2 className="section-title">Legal Basis for Processing</h2>
          <p>
            Where applicable under relevant law, we process personal information on one or more
            of the following grounds:
          </p>
          <ul>
            <li>Your consent</li>
            <li>
              Performance of a contract or steps taken at your request before entering a
              contract
            </li>
            <li>Compliance with legal or regulatory obligations</li>
            <li>
              Legitimate interests such as service improvement, fraud prevention, platform
              security, and responsible operations
            </li>
          </ul>
        </div>

        {/* Information from Employers */}
        <div className="doc-section">
          <h2 className="section-title">
            Information from Employers and Workplace Partners
          </h2>
          <p>
            Because MegaTransact begins with salary-based lending, some information may be
            received from employers or workplace partners where this is necessary and lawful.
          </p>
          <p>
            Such information may include confirmation of employment, salary-related details,
            repayment status, or payroll-related data needed to administer the service.
          </p>
          <p>
            MegaTransact expects workplace partners to share only the information necessary for
            the relevant service and to do so in accordance with applicable law and their own
            obligations to staff.
          </p>
        </div>

        {/* How We Share Information */}
        <div className="doc-section">
          <h2 className="section-title">How We Share Information</h2>
          <p>We do not sell your personal information.</p>
          <p>
            We may share information only where reasonably necessary, including with:
          </p>
          <ul>
            <li>
              Employers or payroll partners involved in salary-linked arrangements
            </li>
            <li>Payment processors or payout partners</li>
            <li>Identity verification providers</li>
            <li>Hosting and cloud service providers</li>
            <li>Analytics and technical service providers</li>
            <li>
              Professional advisers such as lawyers, auditors, or compliance consultants
            </li>
            <li>
              Regulators, courts, law enforcement, or public authorities where required by law
            </li>
            <li>
              Potential investors, acquirers, or restructuring parties, in a limited and
              appropriate manner, if MegaTransact undergoes a business transition
            </li>
          </ul>
          <p>
            Any sharing will be limited to what is reasonably necessary for the relevant purpose.
          </p>
        </div>

        {/* Data Retention */}
        <div className="doc-section">
          <h2 className="section-title">Data Retention</h2>
          <p>
            MegaTransact keeps personal information only for as long as necessary for the
            purposes described in this Privacy Policy, including for service delivery,
            recordkeeping, dispute resolution, fraud prevention, and legal or regulatory
            compliance.
          </p>
          <p>Retention periods may vary depending on:</p>
          <ul>
            <li>The nature of the information</li>
            <li>The service provided</li>
            <li>Legal requirements</li>
            <li>Accounting and audit obligations</li>
            <li>Fraud prevention needs</li>
            <li>Dispute or enforcement considerations</li>
          </ul>
          <p>
            When information is no longer required, we will delete it, anonymize it, or securely
            store it only where continued retention is legally necessary.
          </p>
        </div>

        {/* Data Security */}
        <div className="doc-section">
          <h2 className="section-title">Data Security</h2>
          <p>
            MegaTransact takes reasonable technical, administrative, and organizational steps to
            protect personal information from unauthorized access, loss, misuse, alteration, or
            disclosure.
          </p>
          <p>These measures may include:</p>
          <ul>
            <li>Access controls</li>
            <li>Encryption where appropriate</li>
            <li>Secure storage</li>
            <li>Audit logs</li>
            <li>Staff confidentiality obligations</li>
            <li>Limited role-based access</li>
            <li>Monitoring for suspicious activity</li>
          </ul>
          <p>
            However, no method of transmission or storage is completely secure, and MegaTransact
            cannot guarantee absolute security.
          </p>
        </div>

        {/* International Data Transfers */}
        <div className="doc-section">
          <h2 className="section-title">International Data Transfers</h2>
          <p>
            Your information may be stored or processed in countries other than your own,
            including where MegaTransact or its service providers operate infrastructure or
            support functions.
          </p>
          <p>
            Where personal information is transferred across borders, MegaTransact will take
            reasonable steps to ensure that appropriate safeguards are in place and that the
            information continues to receive an appropriate level of protection.
          </p>
        </div>

        {/* Your Rights */}
        <div className="doc-section">
          <h2 className="section-title">Your Rights</h2>
          <p>
            Depending on the law that applies to you, you may have the right to:
          </p>
          <ul>
            <li>Request access to your personal information</li>
            <li>Request correction of inaccurate or incomplete information</li>
            <li>Request deletion of certain information</li>
            <li>Object to certain processing</li>
            <li>Request restriction of processing</li>
            <li>Withdraw consent where processing depends on consent</li>
            <li>Request transfer of your information where applicable</li>
            <li>Lodge a complaint with a relevant data protection authority</li>
          </ul>
          <p>
            These rights are not absolute and may be limited by law, contractual obligations, or
            legitimate operational requirements.
          </p>
          <p>To exercise your rights, contact us at:</p>
          <div className="doc-contact-box">
            <p>
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
          </div>
        </div>

        {/* Cookies */}
        <div className="doc-section">
          <h2 className="section-title">Cookies and Similar Technologies</h2>
          <p>
            MegaTransact may use cookies, device storage, analytics tools, and similar
            technologies on its website or app to:
          </p>
          <ul>
            <li>Keep users signed in where applicable</li>
            <li>Remember preferences</li>
            <li>Understand service usage</li>
            <li>Improve functionality and stability</li>
            <li>Support security and fraud prevention</li>
          </ul>
          <p>
            You may be able to control some of these technologies through your browser or device
            settings.
          </p>
        </div>

        {/* Children's Privacy */}
        <div className="doc-section">
          <h2 className="section-title">Children's Privacy</h2>
          <p>
            MegaTransact services are not intended for children unless expressly stated otherwise.
            We do not knowingly collect personal information from children in violation of
            applicable law.
          </p>
          <p>
            If we become aware that such information has been collected improperly, we will take
            reasonable steps to delete it.
          </p>
        </div>

        {/* Third-Party Services */}
        <div className="doc-section">
          <h2 className="section-title">Third-Party Services</h2>
          <p>
            MegaTransact may rely on third-party providers for hosting, messaging, verification,
            analytics, payment support, or related technical operations.
          </p>
          <p>
            When you interact with third-party tools or links, their own privacy practices may
            also apply. MegaTransact is not responsible for the privacy practices of independent
            third-party services outside its control.
          </p>
        </div>

        {/* Fraud Prevention */}
        <div className="doc-section">
          <h2 className="section-title">Fraud Prevention and Responsible Use</h2>
          <p>
            MegaTransact may use personal and technical information to detect fraud, misuse,
            duplicate accounts, false submissions, manipulation of service processes, or security
            threats.
          </p>
          <p>
            This is necessary to protect users, employers, partners, and the integrity of the
            platform.
          </p>
        </div>

        {/* Changes */}
        <div className="doc-section">
          <h2 className="section-title">Changes to This Privacy Policy</h2>
          <p>
            MegaTransact may update this Privacy Policy from time to time to reflect service
            developments, legal requirements, or operational improvements.
          </p>
          <p>
            When we make material changes, we will take reasonable steps to notify users
            appropriately, such as through the app, website, or other communication channels.
          </p>
          <p>
            The updated version will become effective from the date shown at the top of the
            policy.
          </p>
        </div>

        {/* Contact */}
        <div className="doc-section">
          <h2 className="section-title">Contact Us</h2>
          <p>
            If you have questions, requests, or concerns about this Privacy Policy or how
            MegaTransact handles personal information, please contact:
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>MegaTransact</strong>
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
